import { useLayoutEffect, useRef } from "react";
import {
  flattenToothNumbers,
  formatModelTypes,
  hasModelCategorySelection,
  hasModelWorkSelection,
  modelCategoryWorkUnits,
  modelDesignerUnits,
  modelOtherItemWorkUnits,
  parseDesignerWork,
  parseModelTypes,
  persistModelTypes,
  sameToothList,
} from "../../utils/jobCard";
import { caseHrefFromAllocationId } from "../../utils/caseIds";
import EmployeeNameEditor, { WrappingSelect } from "./EmployeeNameEditor";
import UnitsModelEditor from "./UnitsModelEditor";

export const splitNames = (value?: string | null) =>
  String(value || "")
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);

export const sumUnits = (units?: Record<string, number> | null) =>
  Object.values(units || {}).reduce((sum, value) => sum + (Number(value) || 0), 0);

/**
 * Same employee designed and passed QC2: the name stays on the case,
 * and only the design units are paid.
 */
export const qcCreditForPerson = (
  qc2: string | null | undefined,
  designerNames: string[],
  designerWork: Record<string, "normal" | "model"> | null | undefined,
  qcUnits: number,
) => {
  const qcKey = (splitNames(qc2)[0] || "").toLowerCase();
  const samePerson = Boolean(
    qcKey &&
      designerNames.some(
        (name) =>
          name.trim().toLowerCase() === qcKey &&
          designerWork?.[name] !== "model",
      ),
  );
  return { units: samePerson ? 0 : qcUnits, samePerson };
};

/** Captain / QC2 units = design work + model work. Delivery units stay separate. */
export const qcUnitsFromAssignedWork = ({
  designerNames,
  designerUnits,
  designerWork,
  hasModel,
  modelUnits,
}: {
  designerNames: string[];
  designerUnits?: Record<string, number> | null;
  designerWork?: Record<string, "normal" | "model"> | null;
  hasModel: boolean;
  modelUnits?: number;
}) => {
  const units = designerUnits || {};
  const work = designerWork || {};
  const design = designerNames.reduce((sum, name) => {
    if (work[name] === "model") return sum;
    return sum + (Number(units[name]) || 0);
  }, 0);
  const extraModel = designerNames
    .filter((name) => work[name] === "model")
    .reduce((sum, name) => sum + Math.max(Number(units[name]) || 0, 1), 0);
  if (!hasModel && !extraModel && !(Number(modelUnits) || 0)) return design;
  return design + extraModel + (Number(modelUnits) || 0);
};

export const sameText = (a: unknown, b: unknown) =>
  String(a ?? "").trim() === String(b ?? "").trim();

export const sameUnitsMap = (
  a?: Record<string, number> | null,
  b?: Record<string, number> | null,
) => {
  const left = a || {};
  const right = b || {};
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (Number(left[key] || 0) !== Number(right[key] || 0)) return false;
  }
  return true;
};

export const formatPeopleUnits = (
  names?: string | null,
  units?: Record<string, number> | null,
) =>
  splitNames(names)
    .map((name) => {
      const count = units?.[name];
      return count != null ? `${name} (${count})` : name;
    })
    .join(", ");

export type IrContributor = {
  name: string;
  work: "Designer" | "QC2";
  case_id: string;
};

export type InternalRedesignUnitsInfo = {
  case_id: string;
  status: string;
  designers: string;
  qc1: string;
  qc2: string;
  design_unit: number;
  units: number;
  qc_units?: number;
  model_units?: number;
  model_by?: string;
  model_types?: string[];
  designer_units: Record<string, number>;
  designer_work?: Record<string, "normal" | "model">;
  categories: string;
  assignment: string;
  same_team: boolean;
  extra_designers: string[];
  extra_qc2: string[];
};

export type UnitsRedesignScope = "Minor" | "Major" | "New Design";

export const UNIT_REDESIGN_SCOPES: UnitsRedesignScope[] = [
  "Minor",
  "Major",
  "New Design",
];

const UNIT_REDESIGN_SCOPE_PRIORITY: UnitsRedesignScope[] = [
  "New Design",
  "Major",
  "Minor",
];

const SCOPE_BADGE_CLASS: Record<UnitsRedesignScope, string> = {
  Minor: "bg-sky-50 text-sky-800",
  Major: "bg-rose-50 text-rose-800",
  "New Design": "bg-emerald-50 text-emerald-800",
};

export const parseUnitsRedesignLabels = (
  value?: string | null,
): { scope: UnitsRedesignScope | ""; extras: string[] } => {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const raw of String(value || "").split(",")) {
    const part = raw.trim();
    if (!part) continue;
    const key = part.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    parts.push(part);
  }
  const scope =
    UNIT_REDESIGN_SCOPE_PRIORITY.find((item) => parts.includes(item)) || "";
  const extras = parts.filter(
    (part) => !UNIT_REDESIGN_SCOPES.includes(part as UnitsRedesignScope),
  );
  return { scope, extras };
};

export const composeUnitsRedesignLabels = (
  scope: UnitsRedesignScope | "",
  extras: string[] = [],
): string => [scope, ...extras].filter(Boolean).join(",");

export const replaceUnitsRedesignScope = (
  labels: string | null | undefined,
  scope: UnitsRedesignScope,
): string => {
  const { extras } = parseUnitsRedesignLabels(labels);
  return composeUnitsRedesignLabels(scope, extras);
};

/** Keep custom units if they already differ from the current type default. */
export const applyClientRedesignScopeChange = ({
  currentLabels,
  nextScope,
  liveDeliveryUnits,
  currentDeliveryUnits,
  designer,
  designerUnits,
}: {
  currentLabels?: string | null;
  nextScope: UnitsRedesignScope;
  liveDeliveryUnits?: number | null;
  currentDeliveryUnits: number;
  designer?: string | null;
  designerUnits?: Record<string, number> | null;
}): {
  nextLabels: string;
  nextDeliveryUnits: number | null;
  nextDesignerUnits: Record<string, number> | null;
} => {
  const nextLabels = replaceUnitsRedesignScope(currentLabels, nextScope);
  const prevSuggested = suggestedRedesignDeliveryUnits(
    currentLabels,
    liveDeliveryUnits,
  );
  const nextSuggested = suggestedRedesignDeliveryUnits(
    nextLabels,
    liveDeliveryUnits,
  );
  const shouldUpdateUnits =
    Number(currentDeliveryUnits) === Number(prevSuggested);
  const firstDesigner = splitNames(designer)[0];
  const currDesignerUnits = designerUnits ?? {};
  const nextDesignerUnits =
    shouldUpdateUnits &&
    firstDesigner &&
    Number(currDesignerUnits[firstDesigner] || 0) ===
      Number(currentDeliveryUnits)
      ? { ...currDesignerUnits, [firstDesigner]: nextSuggested }
      : null;
  return {
    nextLabels,
    nextDeliveryUnits: shouldUpdateUnits ? nextSuggested : null,
    nextDesignerUnits,
  };
};

/** Minor = 1, Major = half of live (rounded), New Design = full live delivery units. */
export const suggestedRedesignDeliveryUnits = (
  labels?: string | null,
  liveDeliveryUnits?: number | null,
): number => {
  const { scope } = parseUnitsRedesignLabels(labels);
  const live = Math.max(0, Number(liveDeliveryUnits) || 0);
  if (scope === "New Design") return live;
  if (scope === "Major") return Math.round(live / 2);
  if (scope === "Minor") return 1;
  return live;
};

export const RedesignScopeTag = ({
  labels,
  className = "",
}: {
  labels?: string | null;
  className?: string;
}) => {
  const { scope } = parseUnitsRedesignLabels(labels);
  if (!scope) return null;
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold leading-tight ${SCOPE_BADGE_CLASS[scope]} ${className}`}
    >
      {scope}
    </span>
  );
};

export const RedesignScopeBadges = ({
  labels,
}: {
  labels?: string | null;
}) => {
  const { scope, extras } = parseUnitsRedesignLabels(labels);
  if (!scope && !extras.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {scope ? <RedesignScopeTag labels={scope} /> : null}
      {extras.map((category) => (
        <span
          key={category}
          className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700"
        >
          {category}
        </span>
      ))}
    </div>
  );
};

export const RedesignScopeEditor = ({
  labels,
  onScopeChange,
}: {
  labels?: string | null;
  onScopeChange: (scope: UnitsRedesignScope) => void;
}) => {
  const { scope, extras } = parseUnitsRedesignLabels(labels);
  return (
    <div className="mt-1 space-y-1">
      <WrappingSelect
        value={scope}
        onChange={(value) => {
          if (UNIT_REDESIGN_SCOPES.includes(value as UnitsRedesignScope)) {
            onScopeChange(value as UnitsRedesignScope);
          }
        }}
        placeholder="Type"
        title={scope || "Redesign type"}
      >
        {!scope ? <option value="">Type</option> : null}
        {UNIT_REDESIGN_SCOPES.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </WrappingSelect>
      {extras.length ? (
        <div className="flex flex-wrap gap-1">
          {extras.map((category) => (
            <span
              key={category}
              className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700"
            >
              {category}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const isLiveUnitsMarked = (unitsBy?: string | null) => {
  const value = String(unitsBy ?? "").trim();
  return Boolean(value) && value !== "N/A";
};

/** Live-case delivery units on a redesign row, so allocators can copy that number. */
export const LiveCaseUnitsHint = ({
  liveDeliveryUnits,
  liveUnitsBy,
}: {
  liveDeliveryUnits?: number | null;
  liveUnitsBy?: string | null;
}) => {
  if (isLiveUnitsMarked(liveUnitsBy)) {
    return (
      <div className="mt-1 inline-flex rounded-full bg-[#F0F7F8] px-2 py-0.5 text-[11px] font-semibold text-[#1F6F7C]">
        Live units: {Number(liveDeliveryUnits) || 0}
      </div>
    );
  }
  return (
    <div className="mt-1 text-[11px] text-[#8A8A85]">no units marked</div>
  );
};

export type InternalUnitCredit = {
  case_id: string;
  unitFor: "Design" | "QC" | "Model";
  name: string;
  units: number;
};

export type InternalRedesignAssignment = {
  case_id: string;
  designer: string;
  captain: string;
  designerUnits: Record<string, number>;
  designerWork: Record<string, "normal" | "model">;
  modelTypes: string[];
  modelBy: string;
  modelUnits: number;
};

type EmpOption = { emp_name: string };

const samePeople = (a?: string | null, b?: string | null) =>
  splitNames(a)
    .map((name) => name.toLowerCase())
    .sort()
    .join("|") ===
  splitNames(b)
    .map((name) => name.toLowerCase())
    .sort()
    .join("|");

export const defaultInternalAssignments = (
  items?: InternalRedesignUnitsInfo[] | null,
): InternalRedesignAssignment[] =>
  (items || []).map((ir) => {
    const designer = ir.designers || "";
    const names = splitNames(designer);
    const modelTypes = parseModelTypes(ir.model_types);
    const hasModelWork =
      hasModelCategorySelection(modelTypes) ||
      hasModelWorkSelection(modelTypes);
    const modelBy = hasModelWork
      ? ir.model_by || names[0] || ""
      : "";
    return {
      case_id: ir.case_id,
      designer,
      captain: splitNames(ir.qc2)[0] || "",
      designerUnits: { ...(ir.designer_units || {}) },
      designerWork: parseDesignerWork(
        ir.designer_work,
        names,
        modelBy,
        hasModelWork,
      ),
      modelTypes,
      modelBy,
      modelUnits: Number(ir.model_units) || 0,
    };
  });

export const serializeInternalAssignments = (
  assignments: InternalRedesignAssignment[],
) =>
  assignments.map((assignment) => {
    const modelTypes = persistModelTypes(
      hasModelCategorySelection(assignment.modelTypes),
      assignment.modelTypes,
    );
    const hasModel = hasModelCategorySelection(modelTypes);
    const hasModelWork = hasModel || hasModelWorkSelection(modelTypes);
    const extraCount = splitNames(assignment.designer).slice(1).length;
    const autoUnits = extraCount
      ? modelCategoryWorkUnits(modelTypes)
      : modelDesignerUnits(hasModel, modelTypes);
    const modelBy = hasModelWork
      ? assignment.modelBy || splitNames(assignment.designer)[0] || ""
      : "";
    return {
      ...assignment,
      model: hasModel ? "Yes" : "No",
      modelTypes,
      modelBy,
      modelUnits: hasModelWork
        ? extraCount || assignment.modelUnits > 0
          ? assignment.modelUnits
          : autoUnits
        : 0,
      designerWork: parseDesignerWork(
        assignment.designerWork,
        splitNames(assignment.designer),
        modelBy,
        hasModelWork,
      ),
    };
  });

export const defaultInternalCredits = (
  items?: InternalRedesignUnitsInfo[] | null,
): InternalUnitCredit[] => {
  const credits: InternalUnitCredit[] = [];
  for (const ir of items || []) {
    if (ir.same_team) continue;
    for (const name of ir.extra_designers || []) {
      const work = ir.designer_work?.[name];
      credits.push({
        case_id: ir.case_id,
        unitFor: work === "model" ? "Model" : "Design",
        name,
        units: Number(ir.designer_units?.[name] ?? ir.units ?? 0) || 0,
      });
    }
    for (const name of ir.extra_qc2 || []) {
      credits.push({
        case_id: ir.case_id,
        unitFor: "QC",
        name,
        units: Number(ir.qc_units || ir.units || 0) || 0,
      });
    }
  }
  return credits;
};

export const InternalRedesignsUnitsPanel = ({
  items,
  assignments,
  onAssignmentsChange,
  parentDesigner,
  parentQc2,
  designerOptions = [],
  qc2Options = [],
  editable = true,
}: {
  items?: InternalRedesignUnitsInfo[] | null;
  assignments?: InternalRedesignAssignment[];
  onAssignmentsChange?: (next: InternalRedesignAssignment[]) => void;
  parentDesigner?: string | null;
  parentQc2?: string | null;
  designerOptions?: EmpOption[];
  qc2Options?: EmpOption[];
  editable?: boolean;
}) => {
  const list = items || [];
  if (!list.length) return null;
  const originals = defaultInternalAssignments(list);
  const currentList = assignments?.length ? assignments : originals;
  const currentById = new Map(currentList.map((row) => [row.case_id, row]));
  const originalById = new Map(originals.map((row) => [row.case_id, row]));

  const setAssignment = (next: InternalRedesignAssignment) => {
    if (!onAssignmentsChange) return;
    onAssignmentsChange(
      list.map((ir) =>
        ir.case_id === next.case_id
          ? next
          : currentById.get(ir.case_id) || originalById.get(ir.case_id)!,
      ),
    );
  };

  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/80 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[13px] font-semibold text-violet-950">
          This case has {list.length} internal redesign
          {list.length === 1 ? "" : "s"}
        </p>
        <p className="text-[11px] text-violet-800">
          Edit IR designer, QC2, and model like the live row. Delivery units
          stay on this live / client redesign row once. QC2 gets design +
          model units, not delivery, unless that person also designed the
          case — then QC2 is recorded and only the design units are paid.
          Extra IR people get their own units when the team is different.
        </p>
      </div>
      <div className="mt-2 grid gap-2">
        {list.map((ir) => {
          const original = originalById.get(ir.case_id)!;
          const assignment = currentById.get(ir.case_id) || original;
          const currModelTypeRows = parseModelTypes(assignment.modelTypes);
          const hasModel = hasModelCategorySelection(currModelTypeRows);
          const defaultModelUnits = modelDesignerUnits(
            hasModel,
            currModelTypeRows,
          );
          const categoryModelUnits = modelCategoryWorkUnits(currModelTypeRows);
          const otherModelUnits = modelOtherItemWorkUnits(currModelTypeRows);
          const extraModelCount = splitNames(assignment.designer).slice(1)
            .length;
          const primaryAutoModelUnits = extraModelCount
            ? categoryModelUnits
            : defaultModelUnits;
          const hasModelWork =
            hasModel || hasModelWorkSelection(currModelTypeRows);
          const currModelUnits = hasModelWork
            ? extraModelCount || assignment.modelUnits > 0
              ? assignment.modelUnits
              : primaryAutoModelUnits
            : 0;
          const currDesignerWork = parseDesignerWork(
            assignment.designerWork,
            splitNames(assignment.designer),
            assignment.modelBy,
            hasModelWork,
          );
          const currQcUnits = qcUnitsFromAssignedWork({
            designerNames: splitNames(assignment.designer),
            designerUnits: assignment.designerUnits,
            designerWork: currDesignerWork,
            hasModel: hasModelWork,
            modelUnits: currModelUnits,
          });
          const qcCredit = qcCreditForPerson(
            assignment.captain,
            splitNames(assignment.designer),
            currDesignerWork,
            currQcUnits,
          );
          const liveSameTeam =
            samePeople(assignment.designer, parentDesigner) &&
            samePeople(assignment.captain, parentQc2);
          const { extras: irCategories } = parseUnitsRedesignLabels(
            ir.categories,
          );
          const designerDirty = !sameText(
            formatPeopleUnits(original.designer, original.designerUnits),
            formatPeopleUnits(assignment.designer, assignment.designerUnits),
          );
          const qc2Dirty = !sameText(original.captain, assignment.captain);
          const modelDirty = !sameText(
            formatModelTypes(original.modelTypes),
            formatModelTypes(currModelTypeRows),
          );

          return (
            <div
              key={ir.case_id}
              className="rounded-lg border border-violet-200 bg-white p-2.5 text-[13px] text-[#4A4A48]"
            >
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={caseHrefFromAllocationId(ir.case_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[#2fa9bf] hover:underline"
                >
                  {ir.case_id}
                </a>
                <RedesignScopeTag labels={ir.categories} />
                {liveSameTeam ? (
                  <span className="rounded-full bg-[#E8F6F8] px-2 py-0.5 text-[11px] font-medium text-[#1A6B78]">
                    Same team — units on this row
                  </span>
                ) : (
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                    Extra people — assign units
                  </span>
                )}
              </div>
              {irCategories.length ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {irCategories.map((category) => (
                    <span
                      key={category}
                      className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              ) : null}
              <div className="mt-1.5 space-y-0.5 text-[12px] leading-snug text-[#6B6B66]">
                {ir.assignment ? <div>Assignment: {ir.assignment}</div> : null}
                <div>Status: {ir.status || "—"}</div>
                {ir.qc1 ? <div>QC1: {ir.qc1}</div> : null}
              </div>

              {editable ? (
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  <div>
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">
                      Designer
                    </div>
                    <EmployeeNameEditor
                      value={assignment.designer}
                      options={designerOptions}
                      placeholder="Designer"
                      showUnits
                      unitsByName={assignment.designerUnits}
                      showModelPerson={hasModelWork}
                      modelBy={assignment.modelBy}
                      modelUnits={currModelUnits}
                      autoModelUnits={primaryAutoModelUnits}
                      autoOtherModelUnits={otherModelUnits}
                      onModelByChange={(name) => {
                        setAssignment({ ...assignment, modelBy: name });
                      }}
                      onModelUnitsChange={(units) => {
                        setAssignment({
                          ...assignment,
                          modelUnits: Math.max(
                            units,
                            extraModelCount ? 0 : 1,
                          ),
                        });
                      }}
                      onWorkChange={(work) => {
                        setAssignment({ ...assignment, designerWork: work });
                      }}
                      onChange={(value) => {
                        setAssignment({ ...assignment, designer: value });
                      }}
                      onUnitsChange={(units) => {
                        setAssignment({ ...assignment, designerUnits: units });
                      }}
                    />
                    {designerDirty ? (
                      <OriginalValue
                        original={formatPeopleUnits(
                          original.designer,
                          original.designerUnits,
                        )}
                        current={formatPeopleUnits(
                          assignment.designer,
                          assignment.designerUnits,
                        )}
                        onRestore={() =>
                          setAssignment({
                            ...assignment,
                            designer: original.designer,
                            designerUnits: original.designerUnits,
                            designerWork: original.designerWork,
                            modelBy: original.modelBy,
                            modelUnits: original.modelUnits,
                          })
                        }
                      />
                    ) : null}
                  </div>

                  <div>
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">
                      QC2
                    </div>
                    <EmployeeNameEditor
                      value={assignment.captain}
                      options={qc2Options}
                      allowMultiple={false}
                      placeholder="QC2"
                      onChange={(value) => {
                        setAssignment({ ...assignment, captain: value });
                      }}
                    />
                    <div className="mt-1 text-[12px] font-medium text-[#4A4A48]">
                      {qcCredit.units} units
                      <span className="ml-1 font-normal text-[#8A8A85]">
                        {qcCredit.samePerson
                          ? "QC2 recorded, design units only"
                          : "(design + model)"}
                      </span>
                    </div>
                    {qc2Dirty ? (
                      <OriginalValue
                        original={original.captain}
                        current={assignment.captain}
                        onRestore={() =>
                          setAssignment({
                            ...assignment,
                            captain: original.captain,
                          })
                        }
                      />
                    ) : null}
                  </div>

                  <div>
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#8A8A85]">
                      Model
                    </div>
                    <UnitsModelEditor
                      value={{
                        model: hasModel ? "Yes" : "No",
                        modelTypes: currModelTypeRows,
                        modelBy: assignment.modelBy,
                      }}
                      onChange={(next) => {
                        const nextHasWork =
                          next.model === "Yes" ||
                          hasModelWorkSelection(next.modelTypes);
                        const nextTypes = persistModelTypes(
                          next.model === "Yes",
                          next.modelTypes,
                        );
                        setAssignment({
                          ...assignment,
                          modelTypes: nextTypes,
                          modelBy: nextHasWork
                            ? assignment.modelBy ||
                              splitNames(assignment.designer)[0] ||
                              ""
                            : "",
                          modelUnits: nextHasWork
                            ? assignment.modelUnits
                            : 0,
                        });
                      }}
                    />
                    {modelDirty ? (
                      <OriginalValue
                        original={formatModelTypes(original.modelTypes)}
                        current={formatModelTypes(currModelTypeRows)}
                        onRestore={() =>
                          setAssignment({
                            ...assignment,
                            modelTypes: original.modelTypes,
                            modelBy: original.modelBy,
                            modelUnits: original.modelUnits,
                          })
                        }
                      />
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="mt-1.5 space-y-0.5 text-[12px] leading-snug text-[#6B6B66]">
                  <div>
                    Designer:{" "}
                    {formatPeopleUnits(
                      assignment.designer,
                      assignment.designerUnits,
                    ) || "—"}
                  </div>
                  <div>QC2: {assignment.captain || "—"}</div>
                  <div>
                    Model: {formatModelTypes(currModelTypeRows) || "No"}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const IrContributorsNote = ({
  contributors,
  work,
}: {
  contributors?: IrContributor[] | null;
  work?: IrContributor["work"];
}) => {
  const items = (contributors || []).filter((c) => !work || c.work === work);
  if (!items.length) return null;
  return (
    <div className="mt-1.5 space-y-0.5">
      {items.map((c) => (
        <div
          key={`${c.case_id}-${c.name}-${c.work}`}
          className="text-[11px] leading-snug text-[#8A8A85]"
        >
          {c.name} · {c.work}
          {c.case_id ? ` · ${c.case_id}` : ""}
        </div>
      ))}
    </div>
  );
};

export const AutoGrowField = ({
  value,
  onChange,
  className,
  placeholder,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  className: string;
  placeholder?: string;
  disabled?: boolean;
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className={`${className} block resize-none overflow-hidden leading-snug ${
        disabled ? "cursor-not-allowed opacity-70" : ""
      }`}
    />
  );
};

export const OriginalValue = ({
  original,
  current,
  onRestore,
}: {
  original?: string | number | null;
  current?: string | number | null;
  onRestore: () => void;
}) => {
  if (sameText(original, current)) return null;
  return (
    <button
      type="button"
      title="Restore saved value"
      onClick={onRestore}
      className="mt-1 block w-full rounded-md bg-emerald-50 px-1.5 py-1 text-left text-[12px] leading-snug text-emerald-800 ring-1 ring-emerald-200 hover:bg-emerald-100"
    >
      <span className="font-semibold text-emerald-700">Saved: </span>
      <span className="break-words">{String(original ?? "").trim() || "—"}</span>
    </button>
  );
};

type NoteField = { label: string; value: string };
type NoteSection = { title: string; fields: NoteField[]; paragraphs: string[] };

const parseJobCardNotes = (text: string): NoteSection[] => {
  let source = String(text || "").replace(/\r\n/g, "\n");
  const lineCount = source.split("\n").filter(Boolean).length;
  if (lineCount <= 2 && (source.match(/:\s/g) || []).length >= 3) {
    source = source.replace(/\s+([A-Z][A-Za-z /&()-]{1,35}:)/g, "\n$1");
  }
  const lines = source.split("\n");
  const sections: NoteSection[] = [];
  let current: NoteSection = { title: "Overview", fields: [], paragraphs: [] };

  const flush = () => {
    if (current.fields.length || current.paragraphs.length) {
      sections.push(current);
    }
  };

  const isHeader = (line: string) =>
    line.length > 1 &&
    line.length < 42 &&
    !line.includes(":") &&
    /^[A-Z][\w /&-]*$/.test(line);

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    if (isHeader(line)) {
      flush();
      current = { title: line, fields: [], paragraphs: [] };
      continue;
    }

    const colon = line.indexOf(":");
    if (colon > 0 && colon < 40) {
      const label = line.slice(0, colon).trim();
      const value = line.slice(colon + 1).trim();
      if (/^[A-Za-z][\w /&()-]*$/.test(label) && !/^https?$/i.test(label)) {
        current.fields.push({ label, value });
        continue;
      }
    }

    current.paragraphs.push(line);
  }

  flush();
  return sections;
};

const FIELD_TONES = [
  { box: "bg-sky-50 border-sky-200", label: "text-sky-700" },
  { box: "bg-teal-50 border-teal-200", label: "text-teal-700" },
  { box: "bg-amber-50 border-amber-200", label: "text-amber-800" },
  { box: "bg-violet-50 border-violet-200", label: "text-violet-700" },
  { box: "bg-emerald-50 border-emerald-200", label: "text-emerald-700" },
  { box: "bg-rose-50 border-rose-200", label: "text-rose-700" },
];

export const categoryRowClass = (
  category: string,
  index: number,
  isSelected: boolean,
) => {
  if (isSelected) return "bg-[#C5EAF0] hover:bg-[#B7E3EB]";
  const c = (category || "").toLowerCase();
  if (c.includes("implant") || c.includes("smile"))
    return "bg-sky-50 hover:bg-sky-100/80";
  if (c.includes("grade ii")) return "bg-amber-50 hover:bg-amber-100/80";
  if (c.includes("with model")) return "bg-cyan-50 hover:bg-cyan-100/80";
  if (c.includes("crown")) return "bg-teal-50/80 hover:bg-teal-100/80";
  if (c.includes("appliance")) return "bg-violet-50 hover:bg-violet-100/80";
  if (c.includes("removable")) return "bg-emerald-50 hover:bg-emerald-100/80";
  if (c.includes("framework")) return "bg-indigo-50 hover:bg-indigo-100/80";
  if (c.includes("digital")) return "bg-blue-50 hover:bg-blue-100/80";
  if (c.includes("surgical")) return "bg-rose-50 hover:bg-rose-100/80";
  if (c.includes("other")) return "bg-orange-50 hover:bg-orange-100/80";
  return index % 2 === 0
    ? "bg-[#F3FBFC] hover:bg-[#E4F3F6]"
    : "bg-[#FFF7EB] hover:bg-[#FFEEC9]";
};

export const categoryAccent = (category: string) => {
  const c = (category || "").toLowerCase();
  if (c.includes("implant") || c.includes("smile"))
    return "shadow-[inset_4px_0_0_0_#38bdf8]";
  if (c.includes("grade ii")) return "shadow-[inset_4px_0_0_0_#f59e0b]";
  if (c.includes("with model")) return "shadow-[inset_4px_0_0_0_#22d3ee]";
  if (c.includes("crown")) return "shadow-[inset_4px_0_0_0_#2fa9bf]";
  if (c.includes("appliance")) return "shadow-[inset_4px_0_0_0_#8b5cf6]";
  if (c.includes("removable")) return "shadow-[inset_4px_0_0_0_#10b981]";
  if (c.includes("framework")) return "shadow-[inset_4px_0_0_0_#6366f1]";
  if (c.includes("digital")) return "shadow-[inset_4px_0_0_0_#3b82f6]";
  if (c.includes("surgical")) return "shadow-[inset_4px_0_0_0_#f43f5e]";
  if (c.includes("other")) return "shadow-[inset_4px_0_0_0_#f97316]";
  return "shadow-[inset_4px_0_0_0_#d6d6d0]";
};

export const unitsTableInputClass =
  "w-full max-w-full min-w-0 box-border rounded-lg border border-[#C9D4D6] bg-white px-1.5 py-1.5 text-[14px] text-[#4A4A48] outline-none focus:border-[#2fa9bf] focus:ring-2 focus:ring-[#2fa9bf]/20";
export const unitsTableDirtyInputClass = `${unitsTableInputClass} border-amber-400 bg-amber-50 focus:border-amber-500 focus:ring-amber-300/40`;
export const unitsTableDirtyWrapClass = "rounded-xl ring-2 ring-amber-300";
export const unitsTableMismatchWrapClass =
  "rounded-xl ring-2 ring-rose-400 bg-rose-50/70";
export const differsFromOrderForm = (current: unknown, orderForm: unknown) => {
  const order = flattenToothNumbers(orderForm);
  if (!order) return false;
  return !sameToothList(current, order);
};

export const OrderFormToothAlert = ({
  current,
  orderForm,
}: {
  current?: string | null;
  orderForm?: string | null;
}) => {
  if (!differsFromOrderForm(current, orderForm)) return null;
  return (
    <p
      title={`Order form: ${flattenToothNumbers(orderForm)}`}
      className="mt-1 rounded-md bg-rose-50 px-1.5 py-1 text-[12px] leading-snug text-rose-800 ring-1 ring-rose-200"
    >
      Check vs order form: {flattenToothNumbers(orderForm)}
    </p>
  );
};

export const unitsTableTh =
  "sticky top-0 z-20 py-2.5 px-1.5 text-left text-[16px] font-bold tracking-wide uppercase text-[#8A8A85] leading-tight whitespace-nowrap bg-[#FDFBF2]";
export const unitsTableTd =
  "py-2.5 px-1.5 align-top text-[14px] text-[#4A4A48] whitespace-normal break-words";

export type UnitsNotesModalState = {
  caseId: string;
  rowKey: string;
  notes: string;
  modelNotes: string;
  category: string;
  unitsNotes: string;
  unitsNotesEditable?: boolean;
  showUnitsNotes?: boolean;
};

export type UnitsBillingNoteModalState = {
  caseId: string;
  rowKey: string;
  unitsNotes: string;
};

export const UnitsBillingNoteModal = ({
  notesModal,
  onClose,
  onUnitsNotesChange,
}: {
  notesModal: UnitsBillingNoteModalState;
  onClose: () => void;
  onUnitsNotesChange: (value: string) => void;
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6">
    <div className="flex w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
      <div className="flex items-start justify-between gap-4 bg-[#2fa9bf] px-5 py-4 text-white">
        <div className="min-w-0">
          <h3 className="text-lg font-bold">Billing note</h3>
          <p className="mt-1 text-sm text-white/90">
            Case {notesModal.caseId} — used only on bill generation
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg px-2 text-2xl leading-none text-white/80 hover:bg-white/15 hover:text-white"
          aria-label="Close billing note"
        >
          ✕
        </button>
      </div>

      <div className="space-y-3 bg-[#F7FBFC] p-5">
        <AutoGrowField
          value={notesModal.unitsNotes}
          onChange={onUnitsNotesChange}
          placeholder="Add billing note for this case…"
          className={`${unitsTableInputClass} min-h-[5.5rem] px-3 py-2.5 text-sm`}
        />
        <p className="text-xs text-[#8A8A85]">
          Saved with Assign. Appears in the tooth numbers column on Generate Bill
          only — not in workflow notes.
        </p>
      </div>

      <div className="flex justify-end border-t border-[#E8E8E4] bg-white px-5 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl bg-[#2fa9bf] px-5 py-2 text-sm font-medium text-white hover:bg-[#2296aa]"
        >
          Done
        </button>
      </div>
    </div>
  </div>
);

export const JobCardNotesModal = ({
  notesModal,
  onClose,
  onUnitsNotesChange,
}: {
  notesModal: UnitsNotesModalState;
  onClose: () => void;
  onUnitsNotesChange?: (value: string) => void;
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6">
    <div className="flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
      <div className="flex items-start justify-between gap-4 bg-[#2fa9bf] px-5 py-4 text-white sm:px-6">
        <div className="min-w-0">
          <h3 className="text-xl font-bold">Notes</h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-white/20 px-3 py-1 text-sm font-semibold">
              Case {notesModal.caseId}
            </span>
            {notesModal.category ? (
              <span className="rounded-full bg-amber-200 px-3 py-1 text-xs font-semibold text-amber-900">
                {notesModal.category}
              </span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg px-2 text-2xl leading-none text-white/80 hover:bg-white/15 hover:text-white"
          aria-label="Close notes"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto bg-[#F7FBFC] p-5 sm:p-6">
        {notesModal.notes ? (
          parseJobCardNotes(notesModal.notes).map((section, sIdx) => (
            <section key={`${section.title}-${sIdx}`}>
              <h4 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#2fa9bf]">
                <span className="h-4 w-1.5 rounded-full bg-[#2fa9bf]" />
                {section.title}
              </h4>
              {section.fields.length ? (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {section.fields.map((field, fIdx) => {
                    const tone = FIELD_TONES[fIdx % FIELD_TONES.length];
                    const wide =
                      field.value.length > 80 || /note/i.test(field.label);
                    return (
                      <div
                        key={`${field.label}-${fIdx}`}
                        className={`rounded-xl border p-3.5 shadow-sm ${tone.box} ${
                          wide ? "md:col-span-2" : ""
                        }`}
                      >
                        <p
                          className={`text-[11px] font-semibold uppercase tracking-wide ${tone.label}`}
                        >
                          {field.label}
                        </p>
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm font-medium text-[#2A2A28]">
                          {field.value || "—"}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {section.paragraphs.length ? (
                <div className="mt-3 whitespace-pre-wrap break-words rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-[#4A4A48] shadow-sm">
                  {section.paragraphs.join("\n")}
                </div>
              ) : null}
            </section>
          ))
        ) : (
          <p className="text-sm text-[#8A8A85]">No job card notes.</p>
        )}

        {notesModal.modelNotes ? (
          <section>
            <h4 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-violet-700">
              <span className="h-4 w-1.5 rounded-full bg-violet-500" />
              Model notes
            </h4>
            <div className="whitespace-pre-wrap break-words rounded-xl border border-violet-200 bg-violet-50 p-4 text-sm leading-relaxed text-[#4A4A48] shadow-sm">
              {notesModal.modelNotes}
            </div>
          </section>
        ) : null}

        {notesModal.showUnitsNotes !== false ? (
          <section>
            <h4 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-amber-800">
              <span className="h-4 w-1.5 rounded-full bg-amber-500" />
              Units notes
              {notesModal.unitsNotesEditable ? (
                <span className="font-medium normal-case tracking-normal text-amber-700">
                  (billing only)
                </span>
              ) : null}
            </h4>
            {notesModal.unitsNotesEditable ? (
              <>
                <AutoGrowField
                  value={notesModal.unitsNotes}
                  onChange={(value) => onUnitsNotesChange?.(value)}
                  placeholder="Add notes for this case…"
                  className={`${unitsTableInputClass} min-h-[5.5rem] px-3 py-2.5 text-sm`}
                />
                <p className="mt-1.5 text-xs text-[#8A8A85]">
                  Optional. Billing only — saved when you assign or save this case.
                </p>
              </>
            ) : notesModal.unitsNotes ? (
              <div className="whitespace-pre-wrap break-words rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-[#4A4A48] shadow-sm">
                {notesModal.unitsNotes}
              </div>
            ) : (
              <p className="text-sm text-[#8A8A85]">No units notes.</p>
            )}
          </section>
        ) : null}
      </div>

      <div className="flex justify-end border-t border-[#E8E8E4] bg-white px-5 py-3 sm:px-6">
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl bg-[#2fa9bf] px-5 py-2 text-sm font-medium text-white hover:bg-[#2296aa]"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);
